import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthProvider';
import { useBranchOverride } from './BranchOverrideContext';
import { franchiseApi, type FranchiseAgency } from '../services/franchiseApi';

interface AgencyContextType {
  agency: FranchiseAgency | null;
  loading: boolean;
}

const AgencyContext = createContext<AgencyContextType>({ agency: null, loading: true });

export const useAgency = () => useContext(AgencyContext);

export const AgencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { overrideUuid, overrideEmail } = useBranchOverride();
  const [agency, setAgency] = useState<FranchiseAgency | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAgency = async () => {
      if (!isAuthenticated || !franchiseApi.isConfigured()) {
        setLoading(false);
        return;
      }

      try {
        const agenciesRes = await franchiseApi.getAgencies();
        const emailToLookup = overrideEmail || user?.email;

        if (overrideUuid) {
          const matched = agenciesRes.branches.find(a => a.id === overrideUuid);
          if (matched) setAgency(matched);
        } else if (emailToLookup) {
          const lookup = await franchiseApi.lookupByEmail(emailToLookup);
          if (lookup) {
            const matched = agenciesRes.branches.find(a => a.id === lookup.id);
            if (matched) setAgency(matched);
          }
        }
      } catch {
        // Silently handle
      } finally {
        setLoading(false);
      }
    };

    fetchAgency();
  }, [isAuthenticated, user, overrideUuid, overrideEmail]);

  return (
    <AgencyContext.Provider value={{ agency, loading }}>
      {children}
    </AgencyContext.Provider>
  );
};
